import { useEngagementPermissions } from '@anytag/auth';
import {
  EngagementDetail,
  EngagementCampaignStatus,
  EngagementSocialAcccountBudgetInput,
  ProposalJobDescriptionBreakDownMode,
  useDenyEngagementInfluencerProposalListMutation
} from '@anytag/graphql';
import { SelectionBar, Button } from '@anytag/shared-atoms';
import { useQueryHelper, useToggleState } from '@anytag/shared-hooks';
import { ProposalDownloadButton } from '../../../../shared';
import { useEngagementProposalSearch } from '../../../hooks';
import { BulkEditModal } from './BulkEditModal';
import { useBulkUpdateInfluencerAccountsBudget } from './hooks';

interface SelectInfluencersActionBarProps {
  numberSelected: number;
  onSelectAll: () => void;
  hasAllSelected?: boolean;
  onToggleSelect: () => void;
  engagement: EngagementDetail;
  selectedInfluencerIds: number[];
  jdMode: ProposalJobDescriptionBreakDownMode;
  selectedAccounts: EngagementSocialAcccountBudgetInput[];
}

export const SelectInfluencersActionBar = ({
  jdMode,
  onSelectAll,
  numberSelected,
  hasAllSelected,
  onToggleSelect,
  selectedAccounts,
  selectedInfluencerIds,
  engagement: { id, currency, status, adAgencyMarginRate }
}: SelectInfluencersActionBarProps) => {
  const bulkModal = useToggleState();
  const { t, enqueueSnackbar, invalidateRouteLoader } = useQueryHelper();
  const { isEditMode } = useEngagementProposalSearch();
  const { hideAllowDenyForModal } = useEngagementPermissions();
  const { callDenyEngagementInfluencerProposalList, loading: rejecting } =
    useDenyEngagementInfluencerProposalListMutation({ onCompleted: invalidateRouteLoader });
  const { updateBulkInfluencerAccountsBudget } = useBulkUpdateInfluencerAccountsBudget({
    jdMode,
    engagementId: id,
    selectedAccounts,
    selectedInfluencerIds
  });

  const hasRejectButton =
    !hideAllowDenyForModal &&
    [EngagementCampaignStatus.REVIEWING, EngagementCampaignStatus.DRAFT, EngagementCampaignStatus.ONGOING].includes(
      status
    );

  const handleRejectInfluencers = async () => {
    try {
      await callDenyEngagementInfluencerProposalList({
        variables: {
          input: { campaignId: id, deniedReason: '', influencerIds: selectedInfluencerIds }
        }
      });
      enqueueSnackbar(t('succeededUpdateWhitelist'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  return (
    <>
      <SelectionBar
        disabled={rejecting}
        selectAllProps={{ onClick: onSelectAll }}
        checkboxProps={{
          disabled: rejecting,
          onClick: onToggleSelect,
          indeterminate: !hasAllSelected,
          label: t('Selected', { count: numberSelected })
        }}
      >
        {!isEditMode ? (
          <>
            {hasRejectButton && (
              <Button
                variant="solid"
                color="danger"
                loading={rejecting}
                disabled={rejecting}
                title={t('Button.Reject')}
                onClick={handleRejectInfluencers}
                className="ml-4 px-4"
              />
            )}

            <ProposalDownloadButton
              engagementId={id}
              css={{ marginLeft: 'auto' }}
              influencerIds={selectedInfluencerIds}
            />
          </>
        ) : (
          <Button variant="solid" onClick={bulkModal.open} title={t('Button.Bulk Edit')} className="ml-4 px-4" />
        )}
      </SelectionBar>

      <BulkEditModal
        open={bulkModal.status}
        onClose={bulkModal.close}
        currency={currency || ''}
        hasAdAgencyMarginRate={!!adAgencyMarginRate}
        onSubmit={updateBulkInfluencerAccountsBudget}
      />
    </>
  );
};
